/**
 * exchangePrefill.ts — correct the Exchange counter's prefill from the parent
 * offer, before anyone can send it.
 *
 * 🔴 THE PREFILL IS LOSSY, AND INVISIBLY SO. Tapping Counter on a deal that
 * routes to the Exchange seeds `exch` from a THREADS row (app.js). But
 * mapDealThread builds the seller side as the single anchor listing with cash
 * hardcoded to zero, because the deal-threads wire carries only `side='offered'`
 * items — `offeredItems.ts` filters it, and both builders go through that loader.
 *
 * So a parent offer's REQUESTED extras and its `requestedCashAmount` are both
 * absent from the row, the Exchange opens missing terms that are really there,
 * and re-sending it would post a counter that silently dropped what nobody on
 * this client could see. That is the same failure dealCounter refuses for the
 * seller composer, and it is worse here because the screen looks complete.
 *
 * ✅ THE DATA EXISTS. `GET /api/offers/:id` returns `requestedItems` AND
 * `requestedCashAmount` alongside the offered side, and it was built for this
 * exact job — its own comment reads "needed to re-prefill The Exchange from the
 * counterer's perspective". No server change, no widened deploy.
 *
 * ⚠️ THIS MODULE DOES NOT SEND ANYTHING. `#ex-send` stays guarded and dead. All
 * this does is make what the visitor sees match what the deal actually is, which
 * has to be true BEFORE a send is wired — wiring the button first would have
 * made it work and the terms wrong.
 *
 * ── HOW IT LANDS AFTER HIS HANDLER ──────────────────────────────────────────
 *
 * The listener is capture-phase, so it sees the click BEFORE his target-phase
 * handler seeds `exch`. It does not stop propagation — `[data-counteroffer]` is
 * navigation and is in DELIBERATELY_UNGUARDED for good reason; suppressing it
 * would break the only way into the Exchange. The read is async, so the
 * correction naturally lands after his synchronous seed, and the bridge writer
 * no-ops unless `exch.counterOf` still names the thread that was clicked.
 *
 * 🪤 A SIMPLE offer never reaches this: his router opens the counter SHEET for
 * those and leaves `exch` alone, so `exch.counterOf` does not match and the
 * correction is skipped. That is the guard, not a route check.
 */

export const COUNTER_SELECTOR = "[data-counteroffer]";

interface PrefillWindow extends Window {
  __gbzThreadOfferId?: (threadId: string) => string | null;
  __gbzThreadIsBuyer?: (threadId: string) => boolean | null;
  __gbzCorrectExchPrefill?: (
    threadId: string,
    spec: {
      mineListingIds: string[];
      theirsListingIds: string[];
      myCash: number;
      theirCash: number;
    },
  ) => { applied: boolean; mineResolved: number; mineDropped: number } | null;
}

/** The half of GET /api/offers/:id this reads. */
export interface ApiOfferDetail {
  offer: {
    id: string;
    listingId: string;
    fromUserId: string;
    toUserId: string;
    cashAmount: number | null;
    requestedCashAmount: number | null;
  };
  offeredItems?: Array<{ listingId: string | null }>;
  requestedItems?: Array<{ listingId: string | null }>;
}

/**
 * The two sides of the parent, in listings.id space, plus each side's cash.
 *
 * `buyer` is the canonical offered side; `seller` is the anchor listing plus any
 * requested extras. Kept as a pure function so the direction mapping can be
 * measured without a DOM or a fetch.
 *
 * 🪤 A null listingId is DROPPED, not passed through. An offered item can
 * legitimately have none — an unlisted collection piece the server stored with a
 * null link — but a deck selection is keyed by listings.id, so there is nothing
 * to select for it. Dropping is honest; inventing an id is not. It also means a
 * prefill can be short, which is why the count matters downstream.
 */
export function parentSides(detail: ApiOfferDetail): {
  buyerListingIds: string[];
  sellerListingIds: string[];
  buyerCash: number;
  sellerCash: number;
} {
  const ids = (rows?: Array<{ listingId: string | null }>) =>
    (rows ?? []).map((r) => r.listingId).filter((id): id is string => !!id);
  return {
    buyerListingIds: ids(detail.offeredItems),
    // The anchor first: the offers route models it as the primary listing and
    // requestedItems as extras BEYOND it, so the deck needs both to show the
    // seller's real side.
    sellerListingIds: [detail.offer.listingId, ...ids(detail.requestedItems)],
    buyerCash: detail.offer.cashAmount ?? 0,
    sellerCash: detail.offer.requestedCashAmount ?? 0,
  };
}

export function installExchangePrefill(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
): () => void {
  /** Threads already corrected, so a second tap does not re-fetch. */
  const done = new Set<string>();

  async function correct(threadId: string): Promise<void> {
    const w = win as PrefillWindow;
    const offerId = w.__gbzThreadOfferId?.(threadId) ?? null;
    const iAmBuyer = w.__gbzThreadIsBuyer?.(threadId) ?? null;
    /* No offer id, or no side: leave his prefill exactly as it is and say
       nothing. This module only ever makes the screen MORE accurate — it has no
       standing to interrupt a navigation, and the send is what refuses. */
    if (!offerId || iAmBuyer === null) return;

    const res = await authFetch(`/api/offers/${encodeURIComponent(offerId)}`);
    if (!res.ok) {
      // Best-effort by design: a failed read leaves the screen as his handler
      // built it, which is where it would have been anyway.
      console.warn(
        "[web-install] exchange prefill: GET /api/offers/" +
          offerId +
          " failed (" +
          res.status +
          ") — the counter opens with the row's partial view",
      );
      return;
    }
    const detail = (await res.json().catch(() => null)) as ApiOfferDetail | null;
    if (!detail?.offer) return;

    const sides = parentSides(detail);
    /* Direction. The decks are "mine" and "theirs" from the viewer's seat, while
       the offer is stored buyer/seller — so which deck holds which side flips
       with who is countering. Same inversion dealCounter makes, read off the
       same stashed flag rather than re-derived. */
    const spec = iAmBuyer
      ? {
          mineListingIds: sides.buyerListingIds,
          theirsListingIds: sides.sellerListingIds,
          myCash: sides.buyerCash,
          theirCash: sides.sellerCash,
        }
      : {
          mineListingIds: sides.sellerListingIds,
          theirsListingIds: sides.buyerListingIds,
          myCash: sides.sellerCash,
          theirCash: sides.buyerCash,
        };

    const applied = w.__gbzCorrectExchPrefill?.(threadId, spec) ?? null;
    if (applied?.mineDropped) {
      /* Named, not silent. A seller-side item with no matching MY_COLLECTION row
         cannot be selected in his deck at all, so the prefill is genuinely short
         and the send will refuse rather than post a partial set. */
      console.warn(
        "[web-install] exchange prefill: " +
          applied.mineDropped +
          " item(s) on your side of the parent offer are not in your collection — " +
          "the counter cannot be composed faithfully and the send will refuse",
      );
    }
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest(COUNTER_SELECTOR) as HTMLElement | null;
    if (!el) return;
    const threadId = el.dataset.counteroffer;
    if (!threadId || done.has(threadId)) return;
    done.add(threadId);
    /* Deliberately not awaited and deliberately not stopping propagation: his
       handler must run, seed exch and navigate exactly as it does today. The
       correction lands when the read resolves. */
    void correct(threadId).catch(() => {
      // A dropped read is not an answer, and not a reason to interrupt. The
      // screen stays as his handler built it.
      done.delete(threadId);
    });
  };

  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
